"use strict";

class SoftFocusHero extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === "true") return;

    this.dataset.initialized = "true";
    this.slides = [...this.querySelectorAll("[data-hero-slide]")];
    this.dots = [...this.querySelectorAll("[data-hero-dot]")];
    this.status = this.querySelector("[data-hero-status]");
    this.toggleButton = this.querySelector("[data-hero-toggle]");
    this.pauseIcon = this.querySelector("[data-pause-icon]");
    this.playIcon = this.querySelector("[data-play-icon]");
    this.current = 0;
    this.timer = null;
    this.pausedByUser = false;
    this.interactionPaused = false;
    this.autoplay = this.dataset.autoplay === "true";
    this.interval = Math.max(5000, Number(this.dataset.changeInterval) || 7000);
    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    this.show(0, false);
    if (this.slides.length < 2) return;

    this.querySelector("[data-hero-prev]")?.addEventListener("click", () => {
      this.show(this.current - 1);
      this.restart();
    });
    this.querySelector("[data-hero-next]")?.addEventListener("click", () => {
      this.show(this.current + 1);
      this.restart();
    });
    this.dots.forEach((dot, index) => {
      dot.addEventListener("click", () => {
        this.show(index);
        this.restart();
      });
    });
    this.toggleButton?.addEventListener("click", () => this.toggleAutoplay());

    this.observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        this.start();
      } else {
        this.stop();
      }
    }, { threshold: 0.25 });
    this.observer.observe(this);

    this.addEventListener("mouseenter", () => {
      this.interactionPaused = true;
      this.stop();
    });
    this.addEventListener("mouseleave", () => {
      this.interactionPaused = false;
      this.start();
    });
    this.addEventListener("focusin", () => {
      this.interactionPaused = true;
      this.stop();
    });
    this.addEventListener("focusout", (event) => {
      if (this.contains(event.relatedTarget)) return;
      this.interactionPaused = false;
      this.start();
    });
    this.addEventListener("shopify:block:select", this.handleBlockSelect);
    document.addEventListener("visibilitychange", this.handleVisibility);
  }

  disconnectedCallback() {
    this.stop();
    this.observer?.disconnect();
    this.removeEventListener("shopify:block:select", this.handleBlockSelect);
    document.removeEventListener("visibilitychange", this.handleVisibility);
  }

  handleVisibility = () => {
    if (document.hidden) {
      this.stop();
    } else {
      this.start();
    }
  };

  handleBlockSelect = (event) => {
    const slide = event.target.closest("[data-hero-slide]");
    const index = this.slides.indexOf(slide);
    if (index >= 0) {
      this.show(index);
      this.stop();
    }
  };

  show(index, announce = true) {
    this.current = (index + this.slides.length) % this.slides.length;
    this.slides.forEach((slide, slideIndex) => {
      const active = slideIndex === this.current;
      slide.classList.toggle("is-active", active);
      slide.setAttribute("aria-hidden", String(!active));
      if ("inert" in slide) slide.inert = !active;
    });
    this.dots.forEach((dot, dotIndex) => {
      dot.setAttribute("aria-pressed", String(dotIndex === this.current));
    });

    if (announce && this.status) {
      this.status.textContent = `Slide ${this.current + 1} of ${this.slides.length}: ${this.slides[this.current].dataset.slideTitle}`;
    }
  }

  start() {
    if (!this.autoplay || this.reducedMotion || this.pausedByUser || this.interactionPaused || this.timer || document.hidden) return;
    this.timer = window.setInterval(() => this.show(this.current + 1, false), this.interval);
  }

  stop() {
    if (!this.timer) return;
    window.clearInterval(this.timer);
    this.timer = null;
  }

  restart() {
    this.stop();
    this.start();
  }

  toggleAutoplay() {
    this.pausedByUser = !this.pausedByUser;
    this.toggleButton.setAttribute("aria-label", this.pausedByUser ? "Play hero autoplay" : "Pause hero autoplay");
    this.pauseIcon.hidden = this.pausedByUser;
    this.playIcon.hidden = !this.pausedByUser;
    if (this.pausedByUser) {
      this.stop();
    } else {
      this.start();
    }
  }
}

if (!customElements.get("soft-focus-hero")) {
  customElements.define("soft-focus-hero", SoftFocusHero);
}
